import type { AnalyticsSearchCategoryResult, AnalyticsTopSearch } from '~/services/AnalyticsService';

export const NLS_SUGGESTIONS_CATEGORY = 'nls_suggestions';

type ClassifiedSearch = AnalyticsTopSearch & {
  searchCategory: string;
};

export function formatAnalyticsCategoryLabel(searchCategory: string) {
  const label = searchCategory.replace(/_/g, ' ');

  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function normalizeAnalyticsText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/** Strips hotel brand tails so "by IHG" and "an IHG Hotel" compare as the same property. */
export function stripHotelBrandSuffixes(normalizedValue: string): string {
  return normalizedValue
    .replace(/\b(?:an|a)\s+[\p{L}\p{N}]+\s+hotel\b/gu, ' ')
    .replace(/\bby\s+[\p{L}\p{N}]+\b/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

const EXACT_MATCH_REVERSE_CONTAINMENT_MIN_RATIO = 0.74;

/** Search-bar UI placeholders collapsed by the analytics BigQuery query; not real NLS. */
const NON_NLS_UI_QUERIES = new Set(['anywhere', 'map area']);

function isContainedMatch(normalizedQuery: string, normalizedName: string): boolean {
  if (!normalizedQuery || !normalizedName) return false;
  if (normalizedName.includes(normalizedQuery)) return true;

  return (
    normalizedQuery.includes(normalizedName) &&
    normalizedName.length / normalizedQuery.length >= EXACT_MATCH_REVERSE_CONTAINMENT_MIN_RATIO
  );
}

export function isNlsSuggestionSearch(naturalLanguageQuery: string, destinationNames: Array<string>): boolean {
  const normalizedQuery = normalizeAnalyticsText(naturalLanguageQuery);
  if (!normalizedQuery || destinationNames.length === 0) {
    return false;
  }

  return destinationNames.some((name) => {
    const normalizedDestination = normalizeAnalyticsText(name);
    if (!normalizedDestination) {
      return false;
    }

    if (normalizedQuery === `${normalizedDestination} hotels with breakfast included`) {
      return true;
    }

    if (normalizedQuery === `family holiday in ${normalizedDestination}`) {
      return true;
    }

    const hotelsInSuffix = ` hotels in ${normalizedDestination}`;
    if (!normalizedQuery.endsWith(hotelsInSuffix)) {
      return false;
    }

    const leadingPhrase = normalizedQuery.slice(0, normalizedQuery.length - hotelsInSuffix.length);
    return leadingPhrase.length > 0;
  });
}

export function isExactMatchSearch(naturalLanguageQuery: string, matchedNames: Array<string>): boolean {
  const normalizedQuery = normalizeAnalyticsText(naturalLanguageQuery);
  if (!normalizedQuery) return false;

  if (NON_NLS_UI_QUERIES.has(normalizedQuery)) return true;

  const strippedQuery = stripHotelBrandSuffixes(normalizedQuery);

  return matchedNames.some((name) => {
    const normalizedName = normalizeAnalyticsText(name);
    if (!normalizedName) return false;
    if (isContainedMatch(normalizedQuery, normalizedName)) return true;

    return isContainedMatch(strippedQuery, stripHotelBrandSuffixes(normalizedName));
  });
}

function sortClassifiedSearches(searches: Array<ClassifiedSearch>): Array<ClassifiedSearch> {
  return [...searches].sort((left, right) => {
    if (right.queryCount !== left.queryCount) {
      return right.queryCount - left.queryCount;
    }

    return left.naturalLanguageQuery.localeCompare(right.naturalLanguageQuery);
  });
}

function groupNlsSuggestions(nlsSuggestions: Array<ClassifiedSearch>): AnalyticsSearchCategoryResult | null {
  if (nlsSuggestions.length === 0) {
    return null;
  }

  const sortedSuggestions = sortClassifiedSearches(nlsSuggestions);

  return {
    searchCategory: NLS_SUGGESTIONS_CATEGORY,
    searchCount: sortedSuggestions.reduce((sum, search) => sum + search.queryCount, 0),
    topSearches: sortedSuggestions.map((search, index) => ({
      ...search,
      rank: index + 1,
    })),
  };
}

function groupExactMatchesByCategory(exactMatches: Array<ClassifiedSearch>): Array<AnalyticsSearchCategoryResult> {
  const searchesByCategory = new Map<string, Array<ClassifiedSearch>>();

  exactMatches.forEach((search) => {
    const categorySearches = searchesByCategory.get(search.searchCategory) ?? [];
    categorySearches.push(search);
    searchesByCategory.set(search.searchCategory, categorySearches);
  });

  return Array.from(searchesByCategory.entries())
    .map(([searchCategory, topSearches]) => ({
      searchCategory,
      searchCount: topSearches.reduce((sum, search) => sum + search.queryCount, 0),
      topSearches: topSearches.map((search, index) => ({
        ...search,
        rank: index + 1,
      })),
    }))
    .sort((left, right) => right.searchCount - left.searchCount);
}

function partitionCategoriesByClassifier(
  categories: Array<AnalyticsSearchCategoryResult>,
  shouldMoveSearch: (search: AnalyticsTopSearch) => boolean,
): {
  remainingCategories: Array<AnalyticsSearchCategoryResult>;
  movedSearches: Array<ClassifiedSearch>;
} {
  const movedSearches: Array<ClassifiedSearch> = [];

  const remainingCategories = categories
    .map((category) => {
      const remainingTopSearches: Array<AnalyticsTopSearch> = [];
      let movedQueryCount = 0;

      category.topSearches.forEach((search) => {
        if (shouldMoveSearch(search)) {
          movedSearches.push({
            ...search,
            searchCategory: category.searchCategory,
          });
          movedQueryCount += search.queryCount;
          return;
        }

        remainingTopSearches.push(search);
      });

      const adjustedSearchCount = Math.max(category.searchCount - movedQueryCount, 0);

      return {
        ...category,
        searchCount: adjustedSearchCount,
        topSearches: remainingTopSearches.map((search, index) => ({
          ...search,
          rank: index + 1,
        })),
      };
    })
    .filter((category) => category.searchCount > 0);

  return { remainingCategories, movedSearches };
}

export function splitExactMatchSearches(categories: Array<AnalyticsSearchCategoryResult>): {
  mainCategories: Array<AnalyticsSearchCategoryResult>;
  exactMatchCategories: Array<AnalyticsSearchCategoryResult>;
  nlsSuggestionCategory: AnalyticsSearchCategoryResult | null;
} {
  const nlsPartition = partitionCategoriesByClassifier(categories, (search) =>
    isNlsSuggestionSearch(
      search.naturalLanguageQuery,
      search.destinations.map((entity) => entity.name),
    ),
  );

  const exactPartition = partitionCategoriesByClassifier(nlsPartition.remainingCategories, (search) =>
    isExactMatchSearch(search.naturalLanguageQuery, [
      ...search.destinations.map((entity) => entity.name),
      ...search.landmarks.map((entity) => entity.name),
      ...search.properties.map((entity) => entity.name),
    ]),
  );

  return {
    mainCategories: exactPartition.remainingCategories,
    exactMatchCategories: groupExactMatchesByCategory(sortClassifiedSearches(exactPartition.movedSearches)),
    nlsSuggestionCategory: groupNlsSuggestions(nlsPartition.movedSearches),
  };
}
